/**
 * `<RemindersBell/>` — the team's UNIFIED attention bell (B4.2 + #907).
 *
 * Issue #768 shipped the 🔔 bell as the reminder feed. Issue #907 folds in-app
 * NOTIFICATIONS into the same bell so there is ONE attention surface: the
 * member's note notifications (someone @mentioned them, or noted on a task they
 * follow) AND every upcoming forward reminder, together. There is no second
 * bell — this is the one place attention gathers.
 *
 * Two calm sections, in attention order:
 *   - **Notifications** (when `task_notes` is on) — the member's OWN note
 *     notifications, newest first. Each row: a glyph by kind (`@` mention /
 *     `💬` subscribed-note), a factual line built from the actor + node title
 *     (+ the note snippet when present), a short team-tz time, and an "open →"
 *     that DEEP-LINKS to that note (opens the node's drawer on its Notes tab,
 *     scrolled to / highlighting the note) AND marks the row read. Unread rows
 *     are visually distinct (a teal dot + a faint tint). A "Mark all read"
 *     affordance clears them.
 *   - **Reminders** (when `task_reminders` is on) — the existing B4.2 feed of
 *     upcoming FORWARD nudges, soonest first; each "open →" jumps to the node.
 *
 * Brand firewall (§A9): the notifications are the member's OWN slice (the
 * backend enforces this — no `member_id` parameter). This is NEVER an activity
 * feed about other people, NEVER a count / tally / ranking about a person. Copy
 * is calm + factual; the automated banlist is substring-based, so phrasing is
 * deliberately about the work + the single actor who wrote the note.
 *
 * The footer's "notification settings" affordance (previously inert) is now an
 * honest explanation popover (#907): there is NO global settings screen — note
 * alerts are turned on per task, in that task's panel — so the popover says
 * exactly that, calmly. Not a dead link, not a fake settings screen. (Wave-5
 * feature-completion may build a real per-event × channel matrix once those
 * channels — email / digest — have a backend write-path; see B4.4 / the ledger.)
 *
 * Editorial revamp (#1043, Wave 3 — B4.3 / B4.4): theme-aware via `useMapTheme()`.
 *   - LEGACY (flag off, every team today): byte-identical to the shipped
 *     soft-mint look — same neutral panel, teal unread dot, glyph + line rows.
 *   - EDITORIAL (flag on): the wireframe `V_B43` inbox — a cream `c.cardBg`
 *     panel on a `c.rule` border, a mono `NOTIFICATIONS` label over a
 *     serif-italic "N new · M read" count, rows led by an in-progress-blue
 *     unread dot + a mono kind tag (MENTION / NOTE teal; BLOCKER reads CALM
 *     AMBER, never red — wired for the Wave-5 kind set), a mono time, and a
 *     `c.bgRecess` footer "scoped to your work" + the honest settings affordance.
 *   Same JSX shape, same data-testids, same copy + behaviour — only the look
 *   forks on theme (RE-SKIN, not rebuild).
 *
 * Presentational over the data hooks (data ⟂ view, §A7): the editor feeds it
 * the notifications + the reminder feed + the node-title resolver + the current
 * member id + the deep-link / mark-read / mark-all callbacks. It owns no
 * fetching and no navigation logic. Degrades calmly (errored / empty → calm
 * notes, never a crash).
 *
 * Isolation: taskmap-local imports only — no scheduler coupling.
 */

import { useState } from "react";

import type { TaskNotification } from "../api/useNotifications";
import type { TaskReminder } from "../api/useReminders";
import { formatEventStamp } from "../history/formatTime";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import { PALETTE } from "../tree/palette";
import { describeNotificationForBell, isUnread } from "./notificationText";
import { describeReminderForBell } from "./reminderText";

/**
 * The mono kind tag a notification row carries in the editorial inbox
 * (wireframe `V_B43` `.t-issue`). Today the data model has only two notification
 * kinds (`mention` / `note_subscribed`, see `notificationText.ts`); the
 * wireframe's richer set (STATE / BLOCKER / UNBLOCKED / STRUCTURE / INVITE) is
 * Wave-5 feature-completion (those events have no notification write-path yet).
 *
 * §A9 / brand firewall: a BLOCKER tag reads CALM AMBER (the blocked state's
 * dot), NEVER alarm red. STATE / UNBLOCKED / DONE-ish read sage; everything
 * else teal. The amber + sage tones are resolved from `theme.stateStyle` so the
 * Wave-5 set inherits them with no new hexes scattered here.
 */
function editorialNotificationTag(
  kind: TaskNotification["kind"],
  theme: MapTheme,
): { label: string; color: string } {
  const c = theme.palette;
  switch (kind) {
    case "mention":
      return { label: "MENTION", color: c.teal };
    default:
      // `note_subscribed` — a note landed on a followed task.
      return { label: "NOTE", color: c.teal };
  }
}

export interface RemindersBellProps {
  /** Whether the notifications section shows (the `task_notes` layer is on). */
  notificationsEnabled: boolean;
  /** The caller's OWN notifications, newest-first (as the endpoint returns). */
  notifications: TaskNotification[];
  /** True while the notification feed is loading. */
  notificationsLoading: boolean;
  /** True on a notification fetch failure (incl. a not-yet-deployed / ungated 404). */
  notificationsError: boolean;
  /** Open a notification — deep-link to its note AND mark it read. */
  onOpenNotification: (notification: TaskNotification) => void;
  /** Mark all of the caller's notifications read. */
  onMarkAllRead: () => void;

  /** Whether the reminders section shows (the `task_reminders` layer is on). */
  remindersEnabled: boolean;
  /** The team's reminders, soonest-first (as the endpoint returns). */
  reminders: TaskReminder[];
  /** True while the reminder feed is loading. */
  isLoading: boolean;
  /** True on a reminder fetch failure (incl. a not-yet-deployed / ungated 404). */
  isError: boolean;

  /** node id → title, to name the task each row points at. */
  nodeTitleById: Map<string, string>;
  /** The signed-in member's id — phrases "is yours" for a nudge aimed at them. */
  currentMembershipId: string | null;
  /** The team's IANA timezone — notification times render in it (#906/#907). */
  teamTimezone?: string | undefined;
  /** Jump to a node (open its drawer / focus it) — a reminder row's "open →". */
  onOpenNode: (nodeId: string) => void;
  /** Close the bell panel. */
  onClose: () => void;
  /**
   * B4.4 (#1043): open the per-event x channel notification-settings matrix.
   * Provided ONLY when the `task_notification_prefs` layer is on for the team —
   * then the footer's "notification settings" affordance LINKS here. When
   * `undefined` (the default — layer off), the footer keeps its honest "there's
   * no separate settings screen" popover.
   */
  onOpenNotificationSettings?: () => void;
}

export function RemindersBell({
  notificationsEnabled,
  notifications,
  notificationsLoading,
  notificationsError,
  onOpenNotification,
  onMarkAllRead,
  remindersEnabled,
  reminders,
  isLoading,
  isError,
  nodeTitleById,
  currentMembershipId,
  teamTimezone,
  onOpenNode,
  onClose,
  onOpenNotificationSettings,
}: RemindersBellProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const hasUnread = notifications.some(isUnread);
  // The serif "N new · M read" count (editorial header, wireframe V_B43). Only
  // meaningful once the feed has resolved; counts WORK on the member's slice,
  // never a tally about a person (§A9).
  const unreadCount = notifications.filter(isUnread).length;
  const readCount = notifications.length - unreadCount;

  return (
    <div
      data-testid="reminders-bell"
      className="flex max-h-full w-[640px] max-w-full flex-col overflow-hidden rounded-xl bg-white"
      style={{
        backgroundColor: editorial ? c.cardBg : "#FFFFFF",
        border: `1px solid ${editorial ? c.rule : PALETTE.softBorder}`,
        boxShadow: editorial
          ? "0 12px 36px rgba(26,22,18,0.16)"
          : "0 2px 16px rgba(31,122,110,0.16)",
      }}
    >
      {/* Header: 🔔 + a unified label + (when there's unread) Mark all read + close.
          Editorial folds the label into a mono `NOTIFICATIONS` kicker over a
          serif-italic "N new · M read" count (V_B43). */}
      <div
        className={
          editorial
            ? "flex items-start gap-2 px-5 pt-4 pb-3"
            : "flex items-center gap-2 px-5 pt-4 pb-3"
        }
      >
        {!editorial && <BellGlyph theme={theme} />}
        {editorial && notificationsEnabled ? (
          <div className="min-w-0">
            <div
              className="text-[10px] uppercase"
              style={{
                color: c.muted,
                fontFamily: theme.fonts.mono,
                letterSpacing: "0.08em",
              }}
            >
              Notifications
            </div>
            <div
              className="text-[22px] leading-tight"
              style={{ color: c.ink, fontFamily: theme.fonts.serif, fontStyle: "italic" }}
              data-testid="notifications-count"
            >
              {unreadCount} new · {readCount} read
            </div>
          </div>
        ) : (
          <span
            className="text-sm font-bold"
            style={{
              color: c.ink,
              ...(editorial ? { fontFamily: theme.fonts.serif, fontStyle: "italic" } : {}),
            }}
          >
            Notifications
          </span>
        )}
        {notificationsEnabled && hasUnread && (
          <button
            type="button"
            data-testid="notifications-mark-all-read"
            onClick={onMarkAllRead}
            className={
              editorial
                ? "ml-auto self-start rounded px-2 py-0.5 text-[11px] underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
                : "ml-2 rounded px-2 py-0.5 text-[11px] transition-colors hover:bg-slate-100"
            }
            style={{ color: editorial ? c.muted : c.teal }}
          >
            {editorial ? "mark all read" : "Mark all read"}
          </button>
        )}
        <button
          type="button"
          data-testid="reminders-bell-close"
          aria-label="Close notifications"
          onClick={onClose}
          className={
            editorial
              ? "self-start rounded p-1 text-base leading-none transition-opacity hover:opacity-70"
              : "ml-auto rounded p-1 text-base leading-none transition-colors hover:bg-slate-100"
          }
          // When the editorial mark-all-read pushed itself to ml-auto, the close
          // button no longer needs it; legacy keeps ml-auto so close hugs the right.
          style={{
            color: c.muted,
            ...(editorial && !(notificationsEnabled && hasUnread) ? { marginLeft: "auto" } : {}),
          }}
        >
          ✕
        </button>
      </div>
      <div className="mx-5 border-t" style={{ borderColor: editorial ? c.rule : "#F3F4F6" }} />

      {/* The feed (scrolls): notifications first, then reminders. */}
      <div className="min-h-0 flex-1 overflow-auto px-5 py-3" data-testid="reminders-bell-feed">
        {notificationsEnabled && (
          <NotificationsSection
            notifications={notifications}
            isLoading={notificationsLoading}
            isError={notificationsError}
            teamTimezone={teamTimezone}
            onOpen={onOpenNotification}
            showHeading={remindersEnabled}
            theme={theme}
          />
        )}

        {remindersEnabled && (
          <RemindersSection
            reminders={reminders}
            isLoading={isLoading}
            isError={isError}
            nodeTitleById={nodeTitleById}
            currentMembershipId={currentMembershipId}
            onOpenNode={onOpenNode}
            showHeading={notificationsEnabled}
            theme={theme}
          />
        )}
      </div>

      {/* Footer: a thin rule + the calm "on your terms" line, with an HONEST
          "notification settings" popover (#907) — note alerts are turned on per
          task, in that task's panel; there is no global settings screen. */}
      <div className="mx-5 border-t" style={{ borderColor: editorial ? c.rule : "#F3F4F6" }} />
      <BellFooter
        theme={theme}
        {...(onOpenNotificationSettings ? { onOpenNotificationSettings } : {})}
      />
    </div>
  );
}

// ── Notifications section (#907) ──────────────────────────────────────────────

function NotificationsSection({
  notifications,
  isLoading,
  isError,
  teamTimezone,
  onOpen,
  showHeading,
  theme,
}: {
  notifications: TaskNotification[];
  isLoading: boolean;
  isError: boolean;
  teamTimezone: string | undefined;
  onOpen: (notification: TaskNotification) => void;
  /** Show a small "Notifications" sub-heading (only when reminders also show). */
  showHeading: boolean;
  theme: MapTheme;
}) {
  return (
    <div data-testid="notifications-section">
      {showHeading && <SectionHeading theme={theme}>Notifications</SectionHeading>}
      {isError ? (
        <SectionUnavailable
          testId="notifications-unavailable"
          label="Notifications unavailable"
          theme={theme}
        />
      ) : isLoading ? (
        <SectionLoading
          testId="notifications-loading"
          label="Loading notifications…"
          theme={theme}
        />
      ) : notifications.length === 0 ? (
        <NotificationsEmpty theme={theme} />
      ) : (
        <ul className="flex flex-col gap-2">
          {notifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              teamTimezone={teamTimezone}
              onOpen={() => onOpen(notification)}
              theme={theme}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  teamTimezone,
  onOpen,
  theme,
}: {
  notification: TaskNotification;
  teamTimezone: string | undefined;
  onOpen: () => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const { glyph, primary, snippet } = describeNotificationForBell(notification);
  const unread = isUnread(notification);
  const time = formatEventStamp(notification.created_at, teamTimezone);
  const tag = editorialNotificationTag(notification.kind, theme);

  return (
    <li
      data-testid="notification-row"
      data-notification-id={notification.id}
      data-unread={unread ? "true" : "false"}
      className={
        editorial
          ? "flex items-start gap-2.5 rounded-[10px] px-3 py-3"
          : "flex items-start gap-3 rounded-lg px-3 py-3"
      }
      // Editorial: unread rows sit on warm paper, read rows are transparent
      // (V_B43). Legacy: unread rows get a faint teal tint; read rows the
      // neutral card bg.
      style={{
        backgroundColor: editorial
          ? unread
            ? c.canvas
            : "transparent"
          : unread
            ? "#ECF7F4"
            : "#F7F8FA",
      }}
    >
      {/* The unread dot. Editorial: an in-progress-blue dot (V_B43 `--st-prog`);
          legacy: the shipped teal dot. */}
      <span
        aria-hidden
        data-testid="notification-unread-dot"
        className={
          editorial
            ? "mt-1.5 h-1.5 w-1.5 shrink-0 self-start rounded-full"
            : "mt-1 h-2.5 w-2.5 shrink-0 self-start rounded-full"
        }
        style={{
          backgroundColor: unread
            ? editorial
              ? theme.stateStyle("in_progress").dot
              : PALETTE.teal
            : "transparent",
        }}
      />
      <div className="min-w-0 flex-1">
        {editorial ? (
          <>
            {/* Editorial: a mono kind tag + a mono time on one line, then the
                factual body line below (V_B43). */}
            <div className="mb-1 flex items-center gap-2">
              <span
                data-testid="notification-kind-tag"
                className="text-[10px] uppercase"
                style={{
                  color: tag.color,
                  fontFamily: theme.fonts.mono,
                  letterSpacing: "0.08em",
                }}
              >
                {tag.label}
              </span>
              <span
                className="ml-auto text-[10px]"
                style={{
                  color: c.faint,
                  fontFamily: theme.fonts.mono,
                  letterSpacing: "0.04em",
                }}
                data-testid="notification-time"
              >
                {time}
              </span>
            </div>
            <p className="text-xs" style={{ color: c.slate }} data-testid="notification-primary">
              <span aria-hidden className="mr-1" style={{ color: c.teal }}>
                {glyph}
              </span>
              {primary}
            </p>
            {snippet && (
              <p
                className="mt-0.5 truncate text-[11px] italic"
                style={{ color: c.muted }}
                data-testid="notification-snippet"
              >
                “{snippet}”
              </p>
            )}
          </>
        ) : (
          <>
            <p
              className="text-xs"
              style={{ color: PALETTE.ink }}
              data-testid="notification-primary"
            >
              <span aria-hidden className="mr-1" style={{ color: PALETTE.teal }}>
                {glyph}
              </span>
              {primary}
            </p>
            {snippet && (
              <p
                className="mt-0.5 truncate text-[11px] italic"
                style={{ color: "#6B7280" }}
                data-testid="notification-snippet"
              >
                “{snippet}”
              </p>
            )}
            <p
              className="mt-0.5 text-[10px]"
              style={{ color: "#9CA3AF" }}
              data-testid="notification-time"
            >
              {time}
            </p>
          </>
        )}
      </div>
      <button
        type="button"
        data-testid="notification-open"
        onClick={onOpen}
        className={
          editorial
            ? "shrink-0 self-center rounded-md px-2 py-1 text-[11px] underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
            : "shrink-0 rounded-md border px-3 py-1 text-[11px] transition-colors hover:bg-white"
        }
        style={
          editorial
            ? { color: c.teal }
            : {
                borderColor: PALETTE.softBorder,
                color: PALETTE.muted,
                backgroundColor: unread ? "#ECF7F4" : "#F7F8FA",
              }
        }
      >
        open →
      </button>
    </li>
  );
}

function NotificationsEmpty({ theme }: { theme: MapTheme }) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div
      data-testid="notifications-empty"
      className="flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
      <span className="text-xl" aria-hidden>
        🔔
      </span>
      <div
        className="text-sm font-semibold"
        style={{
          color: c.slate,
          ...(editorial ? { fontFamily: theme.fonts.serif, fontStyle: "italic" } : {}),
        }}
      >
        Nothing new for you
      </div>
      <div className="max-w-[20rem] text-xs" style={{ color: c.faint }}>
        When someone @mentions you, or notes on a task you follow, it shows here.
      </div>
    </div>
  );
}

// ── Reminders section (B4.2) ──────────────────────────────────────────────────

function RemindersSection({
  reminders,
  isLoading,
  isError,
  nodeTitleById,
  currentMembershipId,
  onOpenNode,
  showHeading,
  theme,
}: {
  reminders: TaskReminder[];
  isLoading: boolean;
  isError: boolean;
  nodeTitleById: Map<string, string>;
  currentMembershipId: string | null;
  onOpenNode: (nodeId: string) => void;
  /** Show a small "Reminders" sub-heading (only when notifications also show). */
  showHeading: boolean;
  theme: MapTheme;
}) {
  return (
    <div data-testid="reminders-section" className={showHeading ? "mt-4" : undefined}>
      {showHeading && <SectionHeading theme={theme}>Reminders</SectionHeading>}
      {isError ? (
        <SectionUnavailable
          testId="reminders-bell-unavailable"
          label="Reminders unavailable"
          theme={theme}
        />
      ) : isLoading ? (
        <SectionLoading testId="reminders-bell-loading" label="Loading reminders…" theme={theme} />
      ) : reminders.length === 0 ? (
        <RemindersEmpty theme={theme} />
      ) : (
        <ul className="flex flex-col gap-2">
          {reminders.map((reminder) => (
            <BellRow
              key={reminder.id}
              reminder={reminder}
              nodeTitle={nodeTitleById.get(reminder.node_id)}
              isMine={
                reminder.target_membership_id != null &&
                reminder.target_membership_id === currentMembershipId
              }
              onOpen={() => onOpenNode(reminder.node_id)}
              theme={theme}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function BellRow({
  reminder,
  nodeTitle,
  isMine,
  onOpen,
  theme,
}: {
  reminder: TaskReminder;
  nodeTitle: string | undefined;
  isMine: boolean;
  onOpen: () => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const { primary, secondary } = describeReminderForBell(reminder, nodeTitle, isMine);
  return (
    <li
      data-testid="reminders-bell-row"
      data-node-id={reminder.node_id}
      className={
        editorial
          ? "flex items-center gap-3 rounded-[10px] px-3 py-3"
          : "flex items-center gap-3 rounded-lg px-3 py-3"
      }
      style={{ backgroundColor: editorial ? c.canvas : "#F7F8FA" }}
    >
      <span
        aria-hidden
        className="mt-0.5 h-2.5 w-2.5 shrink-0 self-start rounded-full"
        style={{ backgroundColor: editorial ? c.teal : PALETTE.teal }}
      />
      <div className="min-w-0 flex-1">
        <p
          className="text-xs"
          style={{ color: editorial ? c.slate : PALETTE.ink }}
          data-testid="reminders-bell-primary"
        >
          {primary}
        </p>
        {secondary && (
          <p
            className="mt-0.5 text-[11px]"
            style={{
              color: editorial ? c.faint : "#9CA3AF",
              ...(editorial ? { fontFamily: theme.fonts.mono, letterSpacing: "0.04em" } : {}),
            }}
            data-testid="reminders-bell-secondary"
          >
            {secondary}
          </p>
        )}
      </div>
      <button
        type="button"
        data-testid="reminders-bell-open"
        onClick={onOpen}
        className={
          editorial
            ? "shrink-0 rounded-md px-2 py-1 text-[11px] underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
            : "shrink-0 rounded-md border px-3 py-1 text-[11px] transition-colors hover:bg-white"
        }
        style={
          editorial
            ? { color: c.teal }
            : {
                borderColor: PALETTE.softBorder,
                color: PALETTE.muted,
                backgroundColor: "#F7F8FA",
              }
        }
      >
        open →
      </button>
    </li>
  );
}

// ── Footer + the "notification settings" affordance (#907 / B4.4 #1043) ───────
// When `onOpenNotificationSettings` is provided (the `task_notification_prefs`
// layer is on for the team), the link OPENS the B4.4 per-event x channel matrix.
// When it is undefined (the layer off — the default), the link keeps its honest
// "there's no separate settings screen" popover (#907). Never a dead link, never
// a fake settings screen.

function BellFooter({
  theme,
  onOpenNotificationSettings,
}: { theme: MapTheme; onOpenNotificationSettings?: () => void }) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const [explaining, setExplaining] = useState(false);
  const matrixLinked = Boolean(onOpenNotificationSettings);
  return (
    <div
      className={
        editorial
          ? "relative flex items-center justify-between px-5 py-2.5 text-[11px]"
          : "relative px-5 py-3 text-[11px] italic"
      }
      style={editorial ? { color: c.muted, backgroundColor: c.bgRecess } : { color: "#9CA3AF" }}
      data-testid="reminders-bell-footer"
    >
      {editorial ? (
        // The wireframe V_B43 footer: a mono italic "scoped to your work" (§A9 —
        // the bell is the member's OWN slice) + the settings affordance.
        <span
          className="text-[10px] uppercase italic"
          style={{ color: c.faint, fontFamily: theme.fonts.mono, letterSpacing: "0.06em" }}
        >
          scoped to your work
        </span>
      ) : (
        <>Reminders are about the work, on your terms. </>
      )}
      <button
        type="button"
        data-testid="notification-settings-link"
        // Layer ON → open the matrix; layer OFF → toggle the honest popover.
        onClick={() => {
          if (onOpenNotificationSettings) onOpenNotificationSettings();
          else setExplaining((v) => !v);
        }}
        aria-expanded={matrixLinked ? undefined : explaining}
        className={
          editorial
            ? "underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
            : "not-italic underline transition-opacity hover:opacity-80"
        }
        style={{ color: c.muted }}
      >
        notification settings
      </button>
      {!matrixLinked && explaining && (
        <>
          {/* Click-away scrim. */}
          <button
            type="button"
            aria-label="Close explanation"
            onClick={() => setExplaining(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            data-testid="notification-settings-popover"
            className={
              editorial
                ? "absolute bottom-full right-5 z-50 mb-1 w-72 rounded-[10px] border p-3 shadow-xl"
                : "absolute bottom-full right-5 z-50 mb-1 w-72 rounded-lg border bg-white p-3 not-italic shadow-xl"
            }
            style={
              editorial
                ? { borderColor: c.rule, backgroundColor: c.cardBg, color: c.slate }
                : { borderColor: PALETTE.softBorder, color: PALETTE.slate }
            }
          >
            <p
              className="text-[12px] font-semibold"
              style={{
                color: c.ink,
                ...(editorial ? { fontFamily: theme.fonts.serif, fontStyle: "italic" } : {}),
              }}
            >
              How note alerts work
            </p>
            <p className="mt-1 text-[11px] leading-relaxed">
              You're always notified when someone @mentions you. Turn on note alerts for a task from
              that task's panel — there's no separate settings screen.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ── Header glyph (the SVG's stroked bell) ─────────────────────────────────────

function BellGlyph({ theme }: { theme: MapTheme }) {
  return (
    <svg
      width="16"
      height="18"
      viewBox="0 0 16 21"
      aria-hidden
      style={{ color: theme.palette.teal }}
      role="presentation"
    >
      <path
        d="M8 0 C3 0 0 4 0 9 L0 16 L16 16 L16 9 C16 4 13 0 8 0 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <line
        x1="5"
        y1="16"
        x2="11"
        y2="16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="8" cy="19" r="2" fill="currentColor" />
    </svg>
  );
}

// ── Shared section bits ───────────────────────────────────────────────────────

function SectionHeading({ children, theme }: { children: React.ReactNode; theme: MapTheme }) {
  const editorial = theme.mode === "editorial";
  return (
    <div
      className="mb-2 text-[11px] font-semibold uppercase tracking-wide"
      style={{
        color: editorial ? theme.palette.muted : "#6B7280",
        letterSpacing: "0.5px",
        ...(editorial ? { fontFamily: theme.fonts.mono, fontSize: 10 } : {}),
      }}
    >
      {children}
    </div>
  );
}

function RemindersEmpty({ theme }: { theme: MapTheme }) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div
      data-testid="reminders-bell-empty"
      className="flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
      <span className="text-xl" aria-hidden>
        🔔
      </span>
      <div
        className="text-sm font-semibold"
        style={{
          color: c.slate,
          ...(editorial ? { fontFamily: theme.fonts.serif, fontStyle: "italic" } : {}),
        }}
      >
        Nothing needs you yet
      </div>
      <div className="max-w-[20rem] text-xs" style={{ color: c.faint }}>
        Upcoming nudges on dates ahead will gather here.
      </div>
    </div>
  );
}

function SectionLoading({
  testId,
  label,
  theme,
}: {
  testId: string;
  label: string;
  theme: MapTheme;
}) {
  return (
    <div
      className="py-10 text-center text-xs"
      style={{ color: theme.palette.faint }}
      data-testid={testId}
    >
      {label}
    </div>
  );
}

function SectionUnavailable({
  testId,
  label,
  theme,
}: {
  testId: string;
  label: string;
  theme: MapTheme;
}) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
      <span className="text-xl" aria-hidden>
        🔔
      </span>
      <div className="text-sm" style={{ color: theme.palette.muted }}>
        {label}
      </div>
    </div>
  );
}
